import React, { useEffect, useState, useCallback, useMemo } from "react";
import combinedServices from "../../../shared/services/user-service";
import userConstants from "../../../shared/constants/user-constants";
import moment from "moment-timezone";
import { ButtonBase } from "@mui/material";

export default function DownloadTransponderExcel(props) {
  const { eventId, runDates, timeZone } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [eventData, setEventData] = useState({
    details: null,
    classes: [],
    participants: [],
    heats: [],
    rounds: [],
    imageStream: "",
    eventName: ""
  });

  const AltImg = "https://nambaweb.s3.ap-south-1.amazonaws.com/frontend-static-image/logo.png";
  const baseURL = userConstants.baseURL;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch all data in parallel where possible
        const [eventRes, heatSheetRes, classesRes] = await Promise.all([
          combinedServices.getClassByEventId(eventId),
          combinedServices.getAllHeatSheet({ event_id: eventId }),
          combinedServices.countByClasses(eventId)
        ]);

        // Process event details
        const eventDetails = eventRes.data;
        const rounds = [];
        for (let i = 1; i <= eventDetails.rounds_per_class; i++) {
          rounds.push(i);
        }

        // Process heat sheet data
        const finalArr = [];
        heatSheetRes.data.forEach(val => {
          val.participants.forEach(itm => {
            if(itm.event_participant_class.participation_status=="ENTERED"){
              finalArr.push({
                class: itm.event_participant_class.class.name,
                class_id: itm.event_participant_class.class.id,
                heat: val.heat_number,
                round: val.round,
                firstname: itm.event_participant_class.event_participant.user.user_firstname,
                lastname: itm.event_participant_class.event_participant.user.user_lastname,
                transponder_id: itm.event_participant_class.transponder_id,
                email: itm.event_participant_class.event_participant.user.user_email,
                phone: itm.event_participant_class.event_participant.user.user_mobile,
                birthday: itm.event_participant_class.event_participant.user.user_dob,
                gender: itm.event_participant_class.event_participant.user.user_sex,
                address1: itm.event_participant_class.event_participant.user.user_addr1,
                address2: itm.event_participant_class.event_participant.user.user_addr2,
                city: itm.event_participant_class.event_participant.user.user_city,
                state: itm.event_participant_class.event_participant.user.user_state,
                zipcode: itm.event_participant_class.event_participant.user.user_zip,
                country: itm.event_participant_class.event_participant.user.user_country,
              });
            }
          });
        });

        finalArr.sort((a, b) => a.heat - b.heat);

        // Process classes data
        const classArr = [];
        const dateArr = [];
        const participantClassData = classesRes.data.participants;

        participantClassData.forEach(classVal => {
          if (classVal.run_date) {
            classArr.push({
              id: classVal.class_id,
              name: classVal.class_name,
              rundate: classVal.run_date,
              event_class_id: classVal.event_class_id,
              order_key: classVal.order_key,
            });

            dateArr.push({
              run_date: classVal.run_date,
              class: classVal.class_name,
            });
          }
        });

        classArr.sort((a, b) => {
          if (a.rundate === b.rundate) {
            return a.order_key - b.order_key;
          }
          return new Date(a.rundate) - new Date(b.rundate);
        });

        const formattedDates = {};
        dateArr.forEach(item => {
          const runDate = item.run_date;
          if (runDate in formattedDates) {
            formattedDates[runDate].push(item.class);
          } else {
            formattedDates[runDate] = [item.class];
          }
        });

        setEventData({
          details: eventDetails,
          classes: classArr,
          participants: finalArr,
          heats: finalArr,
          rounds,
          imageStream: eventDetails.image?.length > 0 ? baseURL + "images/" + eventDetails.image : AltImg,
          eventName: eventDetails.full_title,
          dates: formattedDates
        });

      } catch (err) {
        console.error("Error loading data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [eventId, baseURL]);

  const timeaccotolocation2 = useCallback((x, timeZone) => {
    const utcMoment = moment.utc(x);
    const localMoment = utcMoment.tz(timeZone);
    return localMoment.format("MMM DD YYYY");
  }, []);

  const exportData = useMemo(() => {
    if (!eventData.heats.length || !eventData.classes.length) return [];

    const headerRow = {
      FirstName: "FirstName",
      LastName: "LastName",
      Heat: "Heat",
      Round: "Round",
      TransponderNumber: "TransponderNumber",
      ClassName: "ClassName",
      ClassId: "ClassId",
      Email: "Email",
      PhoneNumber: "PhoneNumber",
      Birthday: "Birthday",
      Gender: "Gender",
      City: "City",
      State: "State",
      ZipCode: "ZipCode",
      Country: "Country",
      Date: "Date"
    };

    const dataRows = eventData.heats.map(x => {
      const classInfo = eventData.classes.find(cls => cls.name === x.class);
      return {
        FirstName: x.firstname,
        LastName: x.lastname,
        Heat: Number(x.heat),
        Round: x.round,
        TransponderNumber: x.transponder_id,
        ClassName: x.class,
        ClassId:x.class_id,
        Email: x.email,
        PhoneNumber: x.phone,
        Birthday: x.birthday,
        Gender: x.gender,
        City: x.city,
        State: x.state,
        ZipCode: x.zipcode,
        Country: x.country,
        Date: classInfo ? timeaccotolocation2(classInfo.rundate, timeZone) : ""
      };
    });

    return [headerRow, ...dataRows];
  }, [eventData.heats, eventData.classes, timeZone, timeaccotolocation2]);

  const handleDownload = useCallback((filename, data) => {
    const blob = new Blob([data], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const simulateButtonClicks = useCallback(() => {
    if (isLoading) {
      alert('Data is still loading, please wait');
      return;
    }

    if (!exportData.length) {
      alert('No data available to download');
      return;
    }

    runDates.forEach(date => {
      const formattedDate = moment(date, "MMM DD, YYYY").format("MMM DD YYYY");
      // const dateFilteredData = exportData
      //   .slice(1)
      //   .filter(data => data.Round === 1 && data.Date === formattedDate);


      const dateFilteredData = exportData
        .slice(1)
        .filter(data => data.Round === 1 && data.Date === formattedDate)
        .filter((item, index, self) =>
          index === self.findIndex(t =>
            t.ClassName === item.ClassName &&
            (item.TransponderNumber
              ? t.TransponderNumber === item.TransponderNumber
              : t.FirstName === item.FirstName && t.LastName === item.LastName)
          )
        );


      if (dateFilteredData.length === 0) {
        console.warn(`No data found for date: ${date}`);
        return;
      }

      const csvHeaders = [
        "FirstName", "LastName", "NickName", "PhoneticName",
        "Ability", "ClassName", "ClassId", "IsPaid", "PillNumber",
        "LocalRegisteredDateTime", "RegistrationNumber",
        "FrameNumber", "PermanentNumber", "PrimaryColor",
        "SecondaryColor", "Manufacturer", "ChassisManufacturer",
        "TransponderNumber", "CarID", "GPSTransponderNumber",
        "SponsorName", "Email", "PhoneNumber", "Birthday",
        "Gender", "Address1", "Address2", "City", "State",
        "ZipCode", "Country", "Hometown", "ClubName",
        "ClubRegion", "AmericanMotorcyclistAssociationNumber",
        "AmericanMotorcyclistAssociationExpirationDate",
        "AcademyOfModelAeronauticsNumber",
        "AcademyOfModelAeronauticsExpirationDate",
        "HAMCallSign", "FAANumber", "LocalMembershipType",
        "LocalMembershipCode", "LocalMembershipExpirationDate"
      ].join(',');

      const csvRows = dateFilteredData.map(item => [
        item.FirstName,
        item.LastName,
        '', // NickName
        '', // PhoneticName
        '', // Ability
        item.ClassName,
        item.ClassId,
        '', // IsPaid
        '', // PillNumber
        '', // LocalRegisteredDateTime
        '', // RegistrationNumber
        '', // FrameNumber
        '', // PermanentNumber
        '', // PrimaryColor
        '', // SecondaryColor
        '', // Manufacturer
        '', // ChassisManufacturer
        item.TransponderNumber,
        '', // CarID
        '', // GPSTransponderNumber
        '', // SponsorName
        item.Email,
        item.PhoneNumber,
        item.Birthday,
        item.Gender,
        item.Address1 || '',
        item.Address2 || '',
        item.City,
        item.State,
        item.ZipCode,
        item.Country
      ].join(','));

      const csvContent = [csvHeaders, ...csvRows].join('\n');
      handleDownload(`${eventData.eventName} ${date}`, csvContent);
    });
  }, [isLoading, exportData, runDates, eventData.eventName, handleDownload]);

  return (
    <ButtonBase
      disableRipple
      disabled={isLoading}
      onClick={simulateButtonClicks}
      sx={{
        marginLeft: "10px",
        display: "inline-block",
        background: "#0095fd",
        fontSize: "16px",
        color: "#fff",
        fontWeight: 600,
        padding: "10px 35px",
        "&:hover": {
          backgroundColor: isLoading ? "#0095fd" : "black",
        },
        opacity: isLoading ? 0.7 : 1,
        cursor: isLoading ? "not-allowed" : "pointer",
      }}
    >
      {isLoading ? "Loading..." : "Transponders"}
    </ButtonBase>
  );
}